import { FieldProps, Field } from 'formik';
import React, { useCallback } from 'react';
import {
  FormPageInputItemMultipleSelection,
  FormPageInputItemMultipleSelectionCheckbox,
  MultipleSelectionProperty,
  FormPageInputData,
} from 'shared';
import { FormFieldProps } from '../shared';
import { useFieldPropsHandler } from '../useFieldPropsHandler';
import { MultipleSelectionInput } from './MultipleSelectionInput';
import { MultipleSelectionInputProps } from './shared';

const VALUE_KEY: keyof Pick<FormPageInputItemMultipleSelection, 'values'> =
  'values';

type Props = FormFieldProps<MultipleSelectionProperty>;

const Container: React.FC<
  Props & {
    fieldProps: FieldProps<
      FormPageInputItemMultipleSelection,
      FormPageInputData
    >;
  }
> = ({ fieldProps, property, design, disabled, submitted }) => {
  const { propertyId } = property;
  const {
    fieldTouched,
    fieldErrorMessages,
    updateFieldValueWithCallback,
    updateFieldTouched,
  } = useFieldPropsHandler<FormPageInputItemMultipleSelection>({
    fieldProps,
    propertyId,
  });
  const updateSubtextValue = useCallback(
    (updateValue: number, updateSubtext: string) => {
      updateFieldValueWithCallback((currentPropertyValues) => ({
        ...currentPropertyValues,
        subtexts: (
          currentPropertyValues as FormPageInputItemMultipleSelectionCheckbox
        )?.subtexts?.map((subtext) => ({
          value: subtext.value,
          subtext:
            updateValue === subtext.value ? updateSubtext : subtext.subtext,
        })),
      }));
    },
    [updateFieldValueWithCallback]
  );

  const handleChange: MultipleSelectionInputProps['onSelectChange'] =
    useCallback(
      (updateValue) => {
        updateFieldValueWithCallback((currentPropertyValues) => {
          const { values } = currentPropertyValues;
          if (!values) {
            return {
              ...currentPropertyValues,
              values: [updateValue],
            };
          }
          return {
            ...currentPropertyValues,
            values: values.includes(updateValue)
              ? values.filter((value) => value !== updateValue)
              : [...values, updateValue],
          };
        });
        updateFieldTouched(VALUE_KEY);
      },
      [updateFieldTouched, updateFieldValueWithCallback]
    );
  const handleSubtextChange: MultipleSelectionInputProps['onSubtextChange'] =
    useCallback(
      (updateValue, updateSubtext) => {
        updateSubtextValue(updateValue, updateSubtext);
      },
      [updateSubtextValue]
    );
  const handleSubtextBlur: MultipleSelectionInputProps['onSubtextBlur'] =
    useCallback(
      (updateValue, updateSubtext) => {
        updateSubtextValue(updateValue, updateSubtext);
        updateFieldTouched(VALUE_KEY);
      },
      [updateFieldTouched, updateSubtextValue]
    );

  if (fieldProps.field.value == null) {
    return null;
  }

  return (
    <MultipleSelectionInput
      property={property}
      design={design}
      disabled={disabled}
      value={fieldProps.field.value}
      onSelectChange={handleChange}
      onSubtextChange={handleSubtextChange}
      onSubtextBlur={handleSubtextBlur}
      errorMessages={fieldErrorMessages}
      touched={fieldTouched}
      submitted={submitted}
    />
  );
};

export const MultipleSelection: React.FC<Props> = (props) => {
  return (
    <Field name={props.property.propertyId}>
      {(fieldProps: FieldProps<FormPageInputItemMultipleSelection>) => (
        <Container {...props} fieldProps={fieldProps} />
      )}
    </Field>
  );
};
